import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { User } from '../models/user';
import { GlobalVariable } from '../helpers/settings';
import { Result } from '../models/general';
import { decrypt } from '../helpers/crypto-js';
import { RefreshVerificationService } from './refresh-verification.service';
import { AuthService } from './auth.service';
import { Role } from '../models/user-role';
import { Employee } from '../models/employee';

export interface PaginationData {
	docs: Partial<User>[];
	total: number;
	limit: number;
	page: number;
	pages: number;
}

export interface UserRequestParams {
	roles?: string[];
	projection?: string[];
	population?: any[];
	searchText?: string;
	filters?: any[];
	sort?: string;
}

@Injectable({
	providedIn: 'root'
})
export class UserService {
	readonly BASE_URL = `${GlobalVariable.server.host}/users`;
	public globalUsersInfo = GlobalVariable['USER'];
	private currentEmployee: Employee;

	constructor(
		private httpClient: HttpClient,
		private authService: AuthService,
		private refreshVerifService: RefreshVerificationService
	) {}

	getCurrentUser(): User {
		const auth = localStorage.getItem('auth');
		if (auth) {
			const authObject = JSON.parse(auth);
			authObject.user = JSON.parse(decrypt(authObject?.user));
			return authObject.user;
		} else return null;
	}
	getCurrentUserInfos(): Partial<User> {
		return this.getCurrentUser();
	}
	find(
		isChildComponent: boolean,
		fromConfigModelComponent: boolean,
		fromAppComponent: boolean,
		globalComponentName: string,
		childComponentName: string,
		route,
		projectId: string,
		action: string
	): Observable<Partial<User[]>> {
		let params;
		if (isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName)
				.set('actionName', action);
		}
		if (fromConfigModelComponent) {
			params = new HttpParams()
				.set('fromConfigComponent', fromConfigModelComponent)
				.set('componentRoute', route)
				.set('actionName', action);
		} else if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId)
				.set('actionName', action);
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.get<Partial<User[]>>(`${this.BASE_URL}/find`, { params })
		);
	}

	findByProjectId(
		projectId: string,
		isChildComponent: boolean,
		fromAppComponent: boolean,
		globalComponentName: string,
		childComponentName: string,
		route,
		action: string
	): Observable<Partial<User[]>> {
		let params;
		if (isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName)
				.set('actionName', action);
		}
		if (!isChildComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId)
				.set('actionName', action);
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.get<Partial<{ total: number }>>(`${this.BASE_URL}/find-by-project-id/${projectId}`, {
				params
			})
		);
	}

	findById(
		id: string,
		isChildComponent: boolean,
		fromConfigComponent: boolean,
		fromAppComponent: boolean,
		globalComponentName?: string,
		childComponentName?: string,
		route?: string,
		projectId?: string,
		action?: string,
		userId?: string
	): Observable<Partial<User>> {
		let params;
		if (isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName)
				.set('actionName', action);
		}
		if (fromConfigComponent) {
			params = new HttpParams()
				.set('fromConfigComponent', fromConfigComponent)
				.set('componentRoute', route)
				.set('actionName', action)
				.set('userId', userId);
		} else if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId)
				.set('actionName', action);
		}
		if (!isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('fromConfigComponent', fromConfigComponent)
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('actionName', action)
				.set('userId', userId);
		}
		if (isChildComponent && fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('fromAppComponent', fromAppComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName)
				.set('projectId', projectId);
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.get<Partial<User>>(`${this.BASE_URL}/find-by-id/${id}`, { params })
		);
	}
	findByManagerId(id: string): Observable<Partial<User>> {
		return this.refreshVerifService.buidResult(
			this.httpClient.get<Partial<User>>(`${this.BASE_URL}/find-by-manager-id/${id}`)
		);
	}

	findAccountManagers(params: UserRequestParams): Observable<User[]> {
		return this.refreshVerifService.buidResult(
			this.httpClient.post<User[]>(`${this.BASE_URL}/find-account-managers`, params)
		);
	}
	findByRoles(params: UserRequestParams): Observable<User[]> {
		return this.refreshVerifService.buidResult(
			this.httpClient.post<User[]>(`${this.BASE_URL}/find-by-roles`, params)
		);
	}

	createAccountManager(user: Partial<User>): Observable<{ done: boolean; message?: string }> {
		return this.refreshVerifService.buidResult(
			this.httpClient.post<{ done: boolean; message?: string }>(`${this.BASE_URL}/create-account-manager`, user)
		);
	}
	create(
		user: Partial<User>,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<{ done: boolean; message?: string }> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.post<{ done: boolean; message?: string }>(`${this.BASE_URL}/create`, user, { params })
		);
	}

	editAccountManager(user: Partial<User>): Observable<Result> {
		return this.refreshVerifService.buidResult(
			this.httpClient.put<Result>(`${this.BASE_URL}/edit-account-manager`, user)
		);
	}
	edit(
		user: Partial<User>,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.put<Result>(`${this.BASE_URL}/edit`, user, { params })
		);
	}

	updateProfileInformation(userInfo: User): Observable<{ done: boolean; message?: string }> {
		const params = new HttpParams()
			.set('fromConfigComponent', true)
			.set('componentRoute', 'account')
			.set('userId', userInfo?._id)
			.set('actionName', 'update-user-info');
		return this.refreshVerifService.buidResult(
			this.httpClient.put<{ done: boolean; message?: string }>(`${this.BASE_URL}/update-user-info`, userInfo, {
				params
			})
		);
	}

	updateSubscription(params: {
		userId: string;
		subscriptionId: string;
	}): Observable<{ done: boolean; message?: string }> {
		return this.refreshVerifService.buidResult(
			this.httpClient.put<{ done: boolean; message?: string }>(`${this.BASE_URL}/update-subscription`, params)
		);
	}

	delete(
		userId: string,
		isChildComponent: boolean,
		globalComponentName: string,
		childComponentName: string
	): Observable<Result> {
		const params = new HttpParams()
			.set('isChildComponent', isChildComponent)
			.set('globalComponentName', globalComponentName)
			.set('childComponentName', childComponentName);
		return this.refreshVerifService.buidResult(
			this.httpClient.delete<Result>(`${this.BASE_URL}/delete/${userId}`, { params })
		);
	}
	deleteAccountManager(userId: string): Observable<Result> {
		return this.refreshVerifService.buidResult(
			this.httpClient.delete<Result>(`${this.BASE_URL}/delete-account-manager/${userId}`)
		);
	}

	emailVerification(data: { userId: string; hashedUniqueString: string }): Observable<{
		done: boolean;
		message?: string;
		expired: boolean;
	}> {
		return this.refreshVerifService.buidResult(
			this.httpClient.post<{
				done: boolean;
				message?: string;
				expired: boolean;
			}>(`${this.BASE_URL}/verify`, data)
		);
	}

	resentEmailVerification(user: User): Observable<{ done: boolean; message?: string; expired: boolean }> {
		return this.refreshVerifService.buidResult(
			this.httpClient.post<{
				done: boolean;
				message?: string;
				expired: boolean;
			}>(`${this.BASE_URL}/resentemail`, user)
		);
	}

	updatePassword(data: { userId: string; newPassword: string }): Observable<{
		done: boolean;
		message?: string;
		expired: boolean;
	}> {
		return this.refreshVerifService.buidResult(
			this.httpClient.put<{
				done: boolean;
				message?: string;
				expired: boolean;
			}>(`${this.BASE_URL}/updatepassword`, data)
		);
	}

	forgotpassword(email: string): Observable<{ done: boolean; message?: string }> {
		return this.refreshVerifService.buidResult(
			this.httpClient.post<{ done: boolean; message?: string }>(`${this.BASE_URL}/forgotpassword`, { email })
		);
	}

	passwordVerification(data: { userId: string; token: string }): Observable<{
		done: boolean;
		message?: string;
		expired: boolean;
	}> {
		return this.refreshVerifService.buidResult(
			this.httpClient.post<{
				done: boolean;
				message?: string;
				expired: boolean;
			}>(`${this.BASE_URL}/resetpassword`, data)
		);
	}
	getNavigationRouteUrlByRole(role: string, userRole?: Role) {
		const currentUser = this.getCurrentUserInfos();
		let redirectUrl = '/dashboard';
		switch (role) {
			case 'Owner':
			case 'SuperAdmin':
			case 'Admin': {
				if (!currentUser?.subscription?.toString()?.length) {
					redirectUrl = '';
				} else {
					redirectUrl = 'admin/projects';
				}

				break;
			}
			case 'User':
				for (let acc of userRole?.access) {
					let isConfig = false;
					if (acc.config === 'config') isConfig = true;
					const firstActiveComp = acc.componentAccess.find(comptAccess => comptAccess.isActive);
					if (firstActiveComp) {
						if (acc.config === 'Config') {
							redirectUrl = 'admin/' + firstActiveComp?.route;
							break;
						} else {
							redirectUrl = 'front/' + firstActiveComp?.route + '/' + acc?.project;
							break;
						}
					} else {
						redirectUrl = 'front/dashboard';
					}
				}
				break;
		}
		return redirectUrl;
	}

	getDeliverers(dataPrams: {
		isChildComponent?: boolean;
		fromConfigModelComponent?: boolean;
		fromAppComponent?: boolean;
		globalComponentName?: string;
		childComponentName?: string;
		route?: string;
		projectId?: string;
		action?: string;
	}): Observable<User[]> {
		const {
			isChildComponent,
			fromConfigModelComponent,
			fromAppComponent,
			globalComponentName,
			childComponentName,
			route,
			projectId,
			action
		} = dataPrams;
		let params;
		if (isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('isChildComponent', isChildComponent)
				.set('globalComponentName', globalComponentName)
				.set('childComponentName', childComponentName)
				.set('actionName', action);
		}
		if (fromConfigModelComponent) {
			params = new HttpParams()
				.set('fromConfigComponent', fromConfigModelComponent)
				.set('componentRoute', route)
				.set('actionName', action);
		} else if (fromAppComponent) {
			params = new HttpParams()
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('projectId', projectId)
				.set('actionName', action);
		}
		if (!isChildComponent && !fromAppComponent) {
			params = new HttpParams()
				.set('fromConfigComponent', fromConfigModelComponent)
				.set('fromAppComponent', fromAppComponent)
				.set('componentRoute', route)
				.set('actionName', action);
		}
		return this.refreshVerifService.buidResult(
			this.httpClient.get<User[]>(`${this.BASE_URL}/get-deliverers/${projectId}`, { params })
		);
	}
}
